import React, { Component } from 'react'
import Layout from './Layout';
import { Input, InputNumber, Switch, Select, Button, Upload, Icon, Breadcrumb, message, Modal, Popover } from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    sortableContainer,
    sortableElement,
} from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { adminRoutes } from '../helpers/adminRoutes';
import { getCategories, addCategory } from '../redux/actions/category';
import { addSupply, resetAddSupplyData } from '../redux/actions/addSupply';
import { viewSupply, resetViewSupply } from '../redux/actions/viewSupply';
import { getSupplies } from '../redux/actions/supply';
import { SERVER_URL } from '../api';
import PatternPicker from './PatternPicker';

const { Option } = Select;
const { TextArea } = Input;

// Same drag-reorder pattern AddPattern.jsx already uses for its own gallery.
const SortableItem = sortableElement(({ value }) => <li className="supply-image-sortable-item">{value}</li>);
const SortableImages = sortableContainer(({ children }) => <ul className="upload-images d-flex" style={{ flexWrap: 'wrap' }}>{children}</ul>);

function blankVariant(position) {
    return {
        _id: null,
        optionLabel: '',
        image: null, // filename (saved), null (use gallery default), or a File (picked, not yet uploaded)
        price: 0,
        regPrice: '',
        onSale: false,
        quantity: '',
        weightOz: '',
        lowStockThreshold: '',
        position: position,
        appliesToPatternIds: [],
    };
}

class AddSupply extends Component {

    constructor(props) {
        super(props)
        this.state = {
            edit: false,
            id: null,
            saving: false,

            name: '',
            description: '',
            categoryId: undefined,
            shopVisible: false,
            appliesToAll: false,
            appliesToPatternIds: [],
            requiresShipping: true,
            position: 0,

            images: [],
            imageFiles: [],

            variants: [blankVariant(0)],
            // Per-row (and one product-level) UI-only flags — whether the
            // "restrict to specific patterns" picker is expanded. Not saved
            // directly; appliesToPatternIds is what's actually persisted.
            showAppliesToPatterns: false,
            variantShowAppliesToPatterns: {},

            categoryModalOpen: false,
            newCategoryName: '',
            savingCategory: false,

            branchLink: '',
            branchLinkLoading: false,
        }
    }

    // Mirrors AddPattern.jsx's generateBranchLink — same Branch API call and
    // save-back endpoint shape, but keyed on supply_id (a distinct data field,
    // not $custom_meta_tags) so the app can tell a supply link apart from a
    // pattern link, since both ids are 24-hex Mongo ObjectIds.
    generateBranchLink = async () => {
        const supplyId = this.state.id;
        if (!supplyId) {
            message.error('No supply ID found');
            return;
        }

        this.setState({ branchLinkLoading: true });

        const supplyName = this.state.name || '';
        // Prefixed distinctly from pattern aliases (which use the bare name)
        // so a supply and a pattern with the same/similar name can never
        // collide on the same Branch alias.
        const alias = 'supply-' + supplyName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
            .substring(0, 42);

        // TODO: point this at the real product landing page once it's live —
        // placeholder path mirrors the pattern desktop_url convention.
        const desktopUrl = 'https://patterns.knitrino.com/product/' + supplyId;

        try {
            const response = await fetch('https://api2.branch.io/v1/url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    branch_key: 'key_live_ej7be3pEtQB2AdIudUfn6pfisudfqvFj',
                    alias: alias || undefined,
                    data: {
                        'supply_id': supplyId,
                        '$desktop_url': desktopUrl,
                        '$og_title': supplyName || 'Knitrino Supply',
                        '$og_description': 'View this item in the Knitrino app',
                    }
                })
            });

            const data = await response.json();

            if (data.url) {
                this.setState({ branchLink: data.url });

                const adminData = JSON.parse(localStorage.getItem('adminData'));
                await fetch(`${SERVER_URL}v1/supply/${supplyId}/branchLink`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-access-token': adminData.token
                    },
                    body: JSON.stringify({ branchLink: data.url })
                });

                navigator.clipboard.writeText(data.url).catch(() => {});
                message.success('Branch link created and saved!');
            } else {
                message.error('Failed to create Branch link');
            }
        } catch (error) {
            console.error('Branch link error:', error);
            message.error('Error creating Branch link');
        } finally {
            this.setState({ branchLinkLoading: false });
        }
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        let { dispatch } = this.props;
        dispatch(getCategories());
        dispatch(getSupplies({ start: 0, length: 0 }));
        this.loadFromRoute();
    }

    // AdminRouter reuses the same mounted AddSupply instance across
    // /edit_supply/:id -> /edit_supply/:otherId navigation (only the route
    // param changes, so React Router doesn't remount) — without this, the
    // form just keeps showing whichever supply was loaded first.
    componentDidUpdate(prevProps) {
        if (prevProps.location.pathname !== this.props.location.pathname) {
            window.scrollTo(0, 0);
            this.loadFromRoute();
        }
    }

    componentWillUnmount() {
        let { dispatch } = this.props;
        dispatch(resetViewSupply());
        dispatch(resetAddSupplyData());
    }

    loadFromRoute = () => {
        let { location } = this.props;
        let path = location.pathname.split('/');
        let id = path[2];
        if (id) {
            this.loadSupply(id);
        } else {
            this.resetToBlank();
        }
    }

    resetToBlank = () => {
        this.setState({
            edit: false,
            id: null,
            name: '',
            description: '',
            categoryId: undefined,
            shopVisible: false,
            appliesToAll: false,
            appliesToPatternIds: [],
            requiresShipping: true,
            position: 0,
            images: [],
            imageFiles: [],
            variants: [blankVariant(0)],
            showAppliesToPatterns: false,
            variantShowAppliesToPatterns: {},
        });
    }

    loadSupply = (id) => {
        let { dispatch } = this.props;
        this.setState({ edit: true, id: id });
        dispatch(viewSupply(id)).then(res => {
            let supply = res && res.data && res.data.data;
            let patternNames = (res && res.data && res.data.patternNames) || {};
            if (!supply) return;

            let hydratePatternIds = (ids) => (ids || [])
                .filter(pid => patternNames[pid])
                .map(pid => ({ _id: pid, patternName: patternNames[pid] }));

            let variants = (supply.variants || []).map(v => ({
                _id: v._id,
                optionLabel: v.optionLabel || '',
                // Defensive: an unresolved "__pending:N" placeholder (saved by
                // an older backend build, before it knew this convention)
                // should fall back to the gallery default, not render broken.
                image: (v.image && String(v.image).startsWith('__pending:')) ? null : (v.image || null),
                price: v.price || 0,
                regPrice: v.regPrice === null || v.regPrice === undefined ? '' : v.regPrice,
                onSale: !!v.onSale,
                quantity: v.quantity === null || v.quantity === undefined ? '' : v.quantity,
                weightOz: v.weightOz === null || v.weightOz === undefined ? '' : v.weightOz,
                lowStockThreshold: v.lowStockThreshold === null || v.lowStockThreshold === undefined ? '' : v.lowStockThreshold,
                position: v.position,
                appliesToPatternIds: hydratePatternIds(v.appliesToPatternIds),
            }));

            let variantShowAppliesToPatterns = {};
            variants.forEach((v, i) => {
                if (v.appliesToPatternIds.length > 0) variantShowAppliesToPatterns[i] = true;
            });

            this.setState({
                name: supply.name || '',
                description: supply.description || '',
                categoryId: supply.categoryId || undefined,
                shopVisible: !!supply.shopVisible,
                appliesToAll: !!supply.appliesToAll,
                appliesToPatternIds: hydratePatternIds(supply.appliesToPatternIds),
                showAppliesToPatterns: hydratePatternIds(supply.appliesToPatternIds).length > 0,
                requiresShipping: supply.requiresShipping !== false,
                position: supply.position !== null && supply.position !== undefined ? supply.position : 0,
                images: supply.images || [],
                variants,
                variantShowAppliesToPatterns,
                imageFiles: [], // don't carry pending files across a route change onto a different supply
                branchLink: supply.branchLink || '',
            });
        })
    }

    setField = (field, value) => {
        this.setState({ [field]: value });
    }

    toggleAppliesToPatterns = (val) => {
        this.setState({ showAppliesToPatterns: val });
        if (!val) this.setField('appliesToPatternIds', []);
    }

    toggleVariantAppliesToPatterns = (index, val) => {
        let { variantShowAppliesToPatterns } = this.state;
        this.setState({ variantShowAppliesToPatterns: { ...variantShowAppliesToPatterns, [index]: val } });
        if (!val) this.updateVariantField(index, 'appliesToPatternIds', []);
    }

    // --- Variant rows ---

    addVariantRow = () => {
        let { variants, variantShowAppliesToPatterns } = this.state;
        let newIndex = variants.length;

        if (variants.length > 0) {
            let first = variants[0];
            let copy = {
                ...blankVariant(newIndex),
                price: first.price,
                regPrice: first.regPrice,
                onSale: first.onSale,
                quantity: first.quantity,
                weightOz: first.weightOz,
                lowStockThreshold: first.lowStockThreshold,
                appliesToPatternIds: first.appliesToPatternIds,
            };
            this.setState({
                variants: [...variants, copy],
                variantShowAppliesToPatterns: { ...variantShowAppliesToPatterns, [newIndex]: !!variantShowAppliesToPatterns[0] },
            });
        } else {
            this.setState({ variants: [blankVariant(0)] });
        }
    }

    removeVariantRow = (index) => {
        let { variants } = this.state;
        if (variants.length <= 1) return;
        this.setState({ variants: variants.filter((_, i) => i !== index) });
    }

    updateVariantField = (index, field, value) => {
        let { variants } = this.state;
        let updated = variants.map((v, i) => i === index ? { ...v, [field]: value } : v);
        this.setState({ variants: updated });
    }

    // A variant's photo is picked from the product's own gallery below, not
    // uploaded separately per option — upload once, then assign. `image` is
    // either an already-saved filename (string), null (use the gallery
    // default), or a File object — a photo just added below in this same
    // session, not yet uploaded. Storing the File reference itself (rather
    // than a position in imageFiles) means it stays correctly assigned even
    // if other pending photos are added/removed before you save; its real
    // index is only resolved at submit time, from wherever it ends up in
    // imageFiles right then.
    setVariantImage = (index, image) => {
        this.updateVariantField(index, 'image', image);
    }

    variantImagePreviewUrl = (image) => {
        if (!image) return null;
        return (image instanceof File) ? URL.createObjectURL(image) : `${SERVER_URL}uploads/${image}`;
    }

    // --- Product images (additive — existing photos are only removed when
    // their own remove button is clicked, never as a side effect of adding
    // new ones) ---

    addProductImages = (info) => {
        let files = (info.fileList || [])
            .map(f => f.originFileObj)
            .filter(Boolean);
        this.setState({ imageFiles: files });
    }

    removeExistingImage = (img) => {
        this.setState({ images: this.state.images.filter(i => i !== img) });
        // Any variant pointing at a removed photo falls back to the gallery default.
        this.setState({ variants: this.state.variants.map(v => v.image === img ? { ...v, image: null } : v) });
    }

    onImagesSortEnd = ({ oldIndex, newIndex }) => {
        this.setState(({ images }) => ({
            images: arrayMove(images, oldIndex, newIndex),
        }));
    }

    // --- Category inline add ---

    openCategoryModal = () => {
        this.setState({ categoryModalOpen: true, newCategoryName: '' });
    }

    saveCategory = () => {
        let { dispatch } = this.props;
        let { newCategoryName } = this.state;
        if (!newCategoryName || !newCategoryName.trim()) {
            message.error('Category name is required.');
            return;
        }
        this.setState({ savingCategory: true });
        dispatch(addCategory({ name: newCategoryName, status: true })).then(res => {
            let created = res && res.data && res.data.data;
            this.setState({ savingCategory: false, categoryModalOpen: false });
            dispatch(getCategories()).then(() => {
                if (created && created._id) {
                    this.setState({ categoryId: created._id });
                }
            });
        }).catch(() => {
            this.setState({ savingCategory: false });
            message.error('Could not save category.');
        })
    }

    // --- Submit ---

    validate = () => {
        let { name, variants } = this.state;
        if (!name || !name.trim()) {
            message.error('Name is required.');
            return false;
        }
        if (!variants || variants.length === 0) {
            message.error('A supply must have at least one option.');
            return false;
        }
        if (variants.length > 1 && variants.some(v => !v.optionLabel || !v.optionLabel.trim())) {
            message.error('Every option needs a label when there is more than one.');
            return false;
        }
        return true;
    }

    handleSubmit = (e) => {
        e.preventDefault();
        if (!this.validate()) return;

        let { dispatch, history } = this.props;
        let { edit, id, name, description, categoryId, shopVisible, appliesToAll,
            appliesToPatternIds, requiresShipping, position, images, imageFiles, variants } = this.state;

        let payload = {
            id,
            name,
            description,
            categoryId,
            shopVisible,
            appliesToAll,
            appliesToPatternIds: appliesToPatternIds.map(p => p._id),
            requiresShipping,
            position,
            existingImages: images,
            imageFiles,
            variants: variants.map((v, i) => ({
                _id: v._id,
                optionLabel: v.optionLabel,
                // A File means "a photo added below this session, not yet
                // uploaded" — resolve it to where it'll actually land in
                // imageFiles (submitted alongside), not a snapshotted index,
                // since other pending photos may have been added/removed since.
                image: (v.image instanceof File)
                    ? (imageFiles.indexOf(v.image) >= 0 ? `__pending:${imageFiles.indexOf(v.image)}` : null)
                    : v.image,
                price: v.price,
                regPrice: v.regPrice === '' ? null : v.regPrice,
                onSale: v.onSale,
                quantity: v.quantity === '' ? null : v.quantity,
                weightOz: v.weightOz === '' ? null : v.weightOz,
                lowStockThreshold: v.lowStockThreshold === '' ? null : v.lowStockThreshold,
                position: i,
                appliesToPatternIds: v.appliesToPatternIds.map(p => p._id),
            })),
        };

        this.setState({ saving: true });
        dispatch(addSupply(payload, edit)).then(() => {
            this.setState({ saving: false });
            history.push(adminRoutes.SUPPLYLIST);
        }).catch(() => {
            this.setState({ saving: false });
            message.error('Something went wrong saving this supply.');
        })
    }

    render() {
        let { categories, suppliesTotal } = this.props;
        let categoryList = (categories && categories.data && categories.data.data) || [];
        // Editing repositions among the supplies that already exist; creating
        // adds one more possible slot (this new one can land anywhere,
        // including past the current last position).
        let recordsTotal = (suppliesTotal && suppliesTotal.data && suppliesTotal.data.recordsTotal) || 0;
        let { edit, name, description, categoryId, shopVisible, appliesToAll, appliesToPatternIds,
            requiresShipping, position, images, imageFiles, variants, saving,
            showAppliesToPatterns, variantShowAppliesToPatterns,
            categoryModalOpen, newCategoryName, savingCategory } = this.state;
        let { branchLink, branchLinkLoading } = this.state;
        let positionOptionsCount = edit ? recordsTotal : recordsTotal + 1;

        return (
            <div className="content-box supplies-page">
                <Layout>
                    <div className="bredcrumb-div">
                        <Breadcrumb separator="-">
                            <Breadcrumb.Item><Link to={adminRoutes.SUPPLYLIST}>Supplies</Link></Breadcrumb.Item>
                            <Breadcrumb.Item>{edit ? 'Edit Supply' : 'Add Supply'}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                    <div className="page-title d-flex">
                        <h2>{edit ? 'Edit Supply' : 'Add Supply'}</h2>
                        {edit && (
                            <div style={{ marginLeft: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Button
                                    type="primary"
                                    onClick={this.generateBranchLink}
                                    loading={branchLinkLoading}
                                >
                                    Generate Branch Link
                                </Button>
                                {branchLink && (
                                    <Input
                                        value={branchLink}
                                        style={{ width: '450px', marginLeft: '10px' }}
                                        readOnly
                                        onClick={(e) => {
                                            e.target.select();
                                            navigator.clipboard.writeText(branchLink);
                                            message.success('Copied!');
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <div className="white-box">
                        <div className="supply-form-section">
                            <div className="supply-form-row">
                                <label style={{ width: '100%' }}>
                                    Name
                                    <Input value={name} onChange={(e) => this.setField('name', e.target.value)} placeholder="Supply name" style={{ marginTop: 6 }} />
                                </label>
                            </div>
                            <div className="supply-form-row">
                                <label style={{ width: '100%' }}>
                                    Description
                                    <TextArea value={description} onChange={(e) => this.setField('description', e.target.value)} rows={3} style={{ marginTop: 6 }} />
                                </label>
                            </div>
                            <div className="supply-form-row">
                                <label>Category</label>
                                <div>
                                    <Select
                                        value={categoryId}
                                        onChange={(val) => this.setField('categoryId', val)}
                                        style={{ width: 200 }}
                                        allowClear
                                    >
                                        {categoryList.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
                                    </Select>
                                    <Button onClick={this.openCategoryModal} style={{ marginLeft: 8 }}>+ Add</Button>
                                </div>
                            </div>
                            <div className="supply-form-row">
                                <label>Position</label>
                                <Select style={{ width: 80 }} value={position} onChange={(val) => this.setField('position', val)}>
                                    {Array.from({ length: positionOptionsCount }, (_, i) => <Option key={i} value={i}>{i + 1}</Option>)}
                                </Select>
                            </div>

                            <div className="supply-form-row">
                                <label>Product photos</label>
                                <div>
                                    <SortableImages axis="xy" onSortEnd={this.onImagesSortEnd}>
                                        {images.map((img, i) => (
                                            <SortableItem key={`img-${img}`} index={i} value={(
                                                <div style={{ position: 'relative' }}>
                                                    <img src={`${SERVER_URL}uploads/${img}`} alt="" style={{ width: 60, height: 60, objectFit: 'cover' }} />
                                                    <Button
                                                        size="small"
                                                        shape="circle"
                                                        icon="close"
                                                        style={{ position: 'absolute', top: -8, right: -8 }}
                                                        onClick={() => this.removeExistingImage(img)}
                                                    />
                                                </div>
                                            )} />
                                        ))}
                                    </SortableImages>
                                    <Upload
                                        multiple
                                        beforeUpload={() => false}
                                        onChange={this.addProductImages}
                                        listType="picture-card"
                                    >
                                        <div><Icon type="plus" /><div>Add photo</div></div>
                                    </Upload>
                                    <p className="supply-form-row-hint">Drag to reorder. Each option below picks one of these photos.</p>
                                </div>
                            </div>

                            <div className="supply-form-row">
                                <label>Show in shop <span className="supply-form-row-hint">Off hides this from the shop grid only — doesn't affect add-ons, those are controlled by pattern matching below</span></label>
                                <Switch checked={shopVisible} onChange={(val) => this.setField('shopVisible', val)} />
                            </div>
                            <div className="supply-form-row">
                                <label>Requires shipping</label>
                                <Switch checked={requiresShipping} onChange={(val) => this.setField('requiresShipping', val)} />
                            </div>

                            <div className="supply-addon-settings">
                                <h4>Add-On Settings</h4>
                                <div className="supply-form-row">
                                    <label>Offer as an add-on for every pattern</label>
                                    <Switch checked={appliesToAll} onChange={(val) => this.setField('appliesToAll', val)} />
                                </div>
                                <div className="supply-form-row" style={{ borderBottom: showAppliesToPatterns ? '1px solid #f0f0f0' : 'none' }}>
                                    <label>Offer as an add-on for specific patterns</label>
                                    <Switch checked={showAppliesToPatterns} onChange={this.toggleAppliesToPatterns} />
                                </div>
                                {showAppliesToPatterns && (
                                    <div className="supply-form-row" style={{ borderBottom: 'none' }}>
                                        <PatternPicker
                                            value={appliesToPatternIds}
                                            onChange={(val) => this.setField('appliesToPatternIds', val)}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <h3>Options</h3>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="option-table">
                                <thead>
                                    <tr>
                                        <th>Photo</th>
                                        <th>Label</th>
                                        <th>Price ($)</th>
                                        <th>On sale?</th>
                                        <th>Qty</th>
                                        <th>Weight (oz)</th>
                                        <th>Low stock @</th>
                                        <th>Add-on for specific patterns?</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {variants.map((variant, index) => (
                                        <React.Fragment key={index}>
                                            <tr>
                                                <td>
                                                    {(images.length === 0 && imageFiles.length === 0) ? (
                                                        <span className="supply-form-row-hint">Add product photos above first</span>
                                                    ) : (
                                                        <Popover
                                                            trigger="click"
                                                            title="Pick a photo"
                                                            content={(
                                                                <div className="option-image-picker">
                                                                    <div
                                                                        className={`option-image-picker-choice ${!variant.image ? 'selected' : ''}`}
                                                                        onClick={() => this.setVariantImage(index, null)}
                                                                    >
                                                                        Use default ({images[0] ? 'first product photo' : 'none yet'})
                                                                    </div>
                                                                    <div className="option-image-picker-grid">
                                                                        {images.map((img) => (
                                                                            <img
                                                                                key={img}
                                                                                src={this.variantImagePreviewUrl(img)}
                                                                                alt=""
                                                                                className={`option-image-picker-thumb ${variant.image === img ? 'selected' : ''}`}
                                                                                onClick={() => this.setVariantImage(index, img)}
                                                                            />
                                                                        ))}
                                                                        {imageFiles.map((file, fi) => (
                                                                            <img
                                                                                key={'pending-' + fi}
                                                                                src={this.variantImagePreviewUrl(file)}
                                                                                alt=""
                                                                                title="Not uploaded yet — will be saved with this option"
                                                                                className={`option-image-picker-thumb pending ${variant.image === file ? 'selected' : ''}`}
                                                                                onClick={() => this.setVariantImage(index, file)}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        >
                                                            {variant.image
                                                                ? <img src={this.variantImagePreviewUrl(variant.image)} alt="" className="option-thumb" />
                                                                : <Button size="small">Pick photo</Button>}
                                                        </Popover>
                                                    )}
                                                </td>
                                                <td>
                                                    <Input
                                                        size="small"
                                                        value={variant.optionLabel}
                                                        onChange={(e) => this.updateVariantField(index, 'optionLabel', e.target.value)}
                                                        placeholder="e.g. Sunshine Yellow"
                                                    />
                                                </td>
                                                <td>
                                                    <InputNumber size="small" min={0} step={0.01} value={variant.price} onChange={(val) => this.updateVariantField(index, 'price', val)} />
                                                </td>
                                                <td>
                                                    <Switch size="small" checked={variant.onSale} onChange={(val) => this.updateVariantField(index, 'onSale', val)} />
                                                    {variant.onSale && (
                                                        <InputNumber size="small" min={0} step={0.01} placeholder="Regular price" style={{ marginTop: 4 }}
                                                            value={variant.regPrice} onChange={(val) => this.updateVariantField(index, 'regPrice', val)} />
                                                    )}
                                                </td>
                                                <td>
                                                    <InputNumber size="small" min={0} placeholder="∞" value={variant.quantity} onChange={(val) => this.updateVariantField(index, 'quantity', val)} />
                                                </td>
                                                <td>
                                                    <InputNumber size="small" min={0} value={variant.weightOz} onChange={(val) => this.updateVariantField(index, 'weightOz', val)} />
                                                </td>
                                                <td>
                                                    <InputNumber size="small" min={0} placeholder="default" value={variant.lowStockThreshold} onChange={(val) => this.updateVariantField(index, 'lowStockThreshold', val)} />
                                                </td>
                                                <td>
                                                    <Switch size="small" checked={!!variantShowAppliesToPatterns[index]} onChange={(val) => this.toggleVariantAppliesToPatterns(index, val)} />
                                                </td>
                                                <td>
                                                    {variants.length > 1 && (
                                                        <Button size="small" icon="delete" onClick={() => this.removeVariantRow(index)} />
                                                    )}
                                                </td>
                                            </tr>
                                            {variantShowAppliesToPatterns[index] && (
                                                <tr>
                                                    <td colSpan={9}>
                                                        <PatternPicker
                                                            value={variant.appliesToPatternIds}
                                                            onChange={(val) => this.updateVariantField(index, 'appliesToPatternIds', val)}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Button onClick={this.addVariantRow} style={{ marginTop: 12 }}>+ Add option</Button>

                        <div className="d-flex justify-content-center" style={{ marginTop: 24 }}>
                            <button type="button" onClick={this.handleSubmit} disabled={saving} className="ant-btn OrangeButton">
                                <span>{edit ? 'Update' : 'Save and Exit'}</span>
                            </button>
                        </div>
                    </div>
                </Layout>

                {/* No onOk/footer — App.css hides the default modal footer app-wide. */}
                <Modal
                    title="Add Category"
                    visible={categoryModalOpen}
                    onCancel={() => this.setState({ categoryModalOpen: false })}
                    footer={null}
                    wrapClassName="supplies-page"
                >
                    <Input
                        value={newCategoryName}
                        onChange={(e) => this.setState({ newCategoryName: e.target.value })}
                        placeholder="Category name"
                    />
                    <div className="popup-btn" style={{ marginTop: 20 }}>
                        <button onClick={this.saveCategory} disabled={savingCategory} className="ant-btn OrangeButton"><span>Save</span></button>
                    </div>
                </Modal>
            </div>
        )
    }
}

const mapStateToProps = ((state) => {
    return {
        categories: state.getCategories.data,
        viewData: state.viewSupply.data,
        suppliesTotal: state.getSupplies.data,
    }
})

export default connect(mapStateToProps)(AddSupply);
